'use client';
// LES SUPPORTS DE MAINTENANCE (spec 002, récit 2 — 04/11/2026).
//
// Une vidéo, une notice versées au dossier de l'appareil, ou un document de la
// GED, se RELIENT à une ou plusieurs lignes du plan. Deux composants :
//
//   · `ListeSupports` — ce qu'une échéance propose (fenêtre « Déclarer
//     faite ») : vidéo chargée au clic seulement (`VideoDemonstration`),
//     fichier versé, document de la GED ouvert en panneau ;
//   · `RelierSupport` — la fenêtre « Relier à une maintenance » de l'onglet
//     Documents et de l'onglet Maintenance d'un appareil (RQ, Métrologie).
//
// La résolution (ligne ∪ pièces reliées à aucune ligne) est faite par le
// serveur (`supportsDeLEcheance`) : l'écran n'en refait aucune.
import React, { useCallback, useEffect, useState } from 'react';
import { useGed } from '@/lib/store';
import { I, Modale, Pastille, useBusy, card } from '@/components/ui';
import { cheminMaintenanceAppareil } from '@/lib/maintenance-appareil';
import { Bouton } from '@/shared/Bouton';
import { VideoDemonstration } from '@/components/VideoDemonstration';
import { libelleTypeSupport } from '@/lib/supports-maintenance';
import type { MaintenanceLigne, RefSupportMaintenance, SupportEcheance } from '@/lib/types';
import type { MaintenanceAppareilReponse, SupportAppareil } from '@/lib/maintenance-appareil';

/** Même adresse que `LienFichier` (EquipFiche) — recopiée pour ne pas créer de cycle d'import. */
const urlPiece = (cle: string, ext: string) => `/api/ged/ged/equip/fichiers/${encodeURIComponent(cle)}/${encodeURIComponent(ext)}`;

const lien: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, fontWeight: 600,
  color: 'var(--color-primary-active)', textDecoration: 'none',
};

/** Un support proposé : vidéo au clic, fichier, lien externe ou document de la GED. */
export function Support({ s, origine }: { s: SupportEcheance; origine?: string }) {
  const { ouvrirPanneau } = useGed();
  let corps: React.ReactNode;
  if (s.type === 'document') {
    corps = (
      <>
        <Bouton taille="sm" variante="discret" onClick={() => ouvrirPanneau({ type: 'doc', valeur: s.code || '', origine: origine || '' })}
          style={{ fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{s.code}</Bouton>
        <span style={{ color: 'var(--text-body)' }}>
          {s.statut === 'introuvable' ? 'document introuvable au catalogue' : s.libelle}
        </span>
        {s.version && <span style={{ color: 'var(--text-muted)' }}>v{s.version}</span>}
      </>
    );
  } else if (s.nature === 'video' && s.url) {
    corps = <VideoDemonstration url={s.url} libelle={s.libelle} />;
  } else if (s.fichierCle && s.ext) {
    corps = <a href={urlPiece(s.fichierCle, s.ext)} target="_blank" rel="noreferrer" style={lien}><I n="doc" s={13} />{s.libelle}</a>;
  } else if (s.url) {
    corps = <a href={s.url} target="_blank" rel="noreferrer" style={lien}><I n="doc" s={13} />{s.libelle} ↗</a>;
  } else {
    corps = <span style={{ color: 'var(--text-body)' }}>{s.libelle} <span style={{ color: 'var(--text-muted)' }}>(référence seule)</span></span>;
  }
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5 }}>
      <Pastille ton="neutral" compacte>{libelleTypeSupport(s)}</Pastille>
      {corps}
      {s.type === 'piece' && s.version && <span style={{ color: 'var(--text-muted)' }}>v{s.version}</span>}
    </div>
  );
}

/** Les supports d'une échéance, dans la fenêtre « Déclarer faite ». Rien s'il n'y en a aucun. */
export function ListeSupports({ supports, origine }: { supports: SupportEcheance[]; origine?: string }) {
  if (!supports.length) return null;
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-strong)' }}>Supports de cette maintenance</span>
      {supports.map((s) => <Support key={s.cle} s={s} origine={origine} />)}
    </div>
  );
}

/**
 * « Relier à une maintenance » : coche les lignes du plan de l'appareil
 * auxquelles ce support sert. La liste cochée est l'état voulu (le serveur
 * retire le support des lignes décochées). Aucune ligne cochée = la pièce
 * redevient commune à toutes les maintenances.
 */
export function RelierSupport({ equipementCode, support, libelle, lignes, onFerme, onFait }: {
  equipementCode: string;
  support: RefSupportMaintenance;
  libelle: string;
  /** Les lignes du plan de l'appareil (actives et archivées). */
  lignes: MaintenanceLigne[];
  onFerme: () => void;
  onFait: () => void;
}) {
  const { smqData } = useGed();
  const { busy, run } = useBusy();
  const cle = support.type === 'piece' ? `piece:${support.id}` : `doc:${support.code}`;
  const porte = (l: MaintenanceLigne) => (l.supports || []).some((x) => (x.type === 'piece' ? `piece:${x.id}` : `doc:${x.code}`) === cle);
  const actives = lignes.filter((l) => l.actif || porte(l));
  const [coches, setCoches] = useState<Set<string>>(() => new Set(actives.filter(porte).map((l) => l.ref)));

  const enregistrer = () => run('relier', async () => {
    const r = await smqData('ged/maintenances/supports/relier', {
      equipementCode, support, lignes: Array.from(coches),
    }, 'Supports de maintenance enregistrés.');
    if (r) { onFait(); onFerme(); }
  });

  return (
    <Modale titre="Relier à une maintenance" sousTitre={libelle} largeur={560} onClose={onFerme}
      pied={(
        <>
          <Bouton taille="sm" onClick={onFerme}>Annuler</Bouton>
          <Bouton variante="primaire" taille="sm" onClick={enregistrer} disabled={busy === 'relier'}>Enregistrer</Bouton>
        </>
      )}>
      <div style={{ display: 'grid', gap: 8, fontSize: 12.5 }}>
        <p style={{ margin: 0, color: 'var(--text-muted)' }}>
          Cochez les maintenances auxquelles ce support sert : la fenêtre « Déclarer faite » de ces maintenances le proposera.
          {support.type === 'piece' && ' Une vidéo ou une notice reliée à aucune maintenance est proposée à toutes celles de l’appareil.'}
        </p>
        {actives.length === 0 && <p style={{ margin: 0 }}>Aucune maintenance planifiée sur cet appareil.</p>}
        {actives.map((l) => (
          <label key={l.documentId} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
            <input type="checkbox" checked={coches.has(l.ref)} style={{ marginTop: 2 }}
              onChange={(ev) => setCoches((c) => {
                const n = new Set(c);
                if (ev.target.checked) n.add(l.ref); else n.delete(l.ref);
                return n;
              })} />
            <span>
              {l.libelle} <span style={{ color: 'var(--text-muted)' }}>· {l.frequenceLibelle} · {l.ref}{l.actif ? '' : ' · retirée du plan'}</span>
            </span>
          </label>
        ))}
      </div>
    </Modale>
  );
}

/**
 * Le plan et les supports d'un appareil, pour l'onglet Documents : à quelles
 * maintenances sert chaque pièce ou document (`sertA`, `commun`), et les
 * lignes à proposer dans « Relier à une maintenance ». Lit la même route que
 * l'onglet Maintenance (`GET ged/maintenances/equipement/:code`).
 * `null` tant que la lecture n'a pas abouti : rien n'est affirmé avant.
 */
export function usePlanSupports(code: string): {
  lignes: MaintenanceLigne[] | null;
  supportDe: (cle: string) => SupportAppareil | undefined;
  recharger: () => void;
} {
  const [rep, setRep] = useState<MaintenanceAppareilReponse | null>(null);
  const [tour, setTour] = useState(0);
  useEffect(() => {
    if (!code) return undefined;
    let vivant = true;
    void fetch(`/api/ged/ged/maintenances/equipement/${encodeURIComponent(code)}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (vivant) setRep(d as MaintenanceAppareilReponse | null); })
      .catch(() => { if (vivant) setRep(null); });
    return () => { vivant = false; };
  }, [code, tour]);
  const supportDe = useCallback((cle: string) => (rep?.supports || []).find((x) => x.cle === cle), [rep]);
  return { lignes: rep ? rep.lignes : null, supportDe, recharger: () => setTour((t) => t + 1) };
}

interface LigneCitante {
  documentId: string; ref: string; libelle: string; equipementCode: string; equipementNom: string;
  frequenceLibelle: string; actif: boolean; support: boolean; source: boolean;
}

/**
 * « Utilisé par le plan de maintenance » — la fiche d'un document de la GED
 * dit quelles lignes du plan s'y réfèrent, comme support ou comme document
 * source (spec 002, EF-008). Lecture `GET ged/documents/:code/maintenances`.
 * Rien ne s'affiche tant que la lecture n'a pas abouti, ni quand aucune ligne
 * ne cite le document : on n'affirme pas « aucune » sur une lecture ratée.
 */
export function UtiliseParLePlan({ code }: { code: string }) {
  const [lignes, setLignes] = useState<LigneCitante[] | null>(null);
  useEffect(() => {
    if (!code) return undefined;
    let vivant = true;
    void fetch(`/api/ged/ged/documents/${encodeURIComponent(code)}/maintenances`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (vivant) setLignes(Array.isArray(d?.lignes) ? d.lignes as LigneCitante[] : null); })
      .catch(() => { if (vivant) setLignes(null); });
    return () => { vivant = false; };
  }, [code]);
  if (!lignes?.length) return null;
  return (
    <div style={{ ...card, padding: '16px 18px' }}>
      <h3 style={{ margin: '0 0 10px', fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>
        Utilisé par le plan de maintenance
      </h3>
      {lignes.map((l) => (
        <div key={l.documentId} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '5px 0', fontSize: 12.5, opacity: l.actif ? 1 : 0.6 }}>
          <a href={cheminMaintenanceAppareil(l.equipementCode)} className="lien-doux"
            style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--color-primary-active)' }}>{l.equipementCode}</a>
          <span style={{ color: 'var(--text-body)' }}>{l.libelle}</span>
          <span style={{ color: 'var(--text-muted)' }}>{l.equipementNom} · {l.frequenceLibelle}</span>
          {l.support && <Pastille ton="neutral" compacte>support</Pastille>}
          {l.source && <Pastille ton="neutral" compacte>document source</Pastille>}
          {!l.actif && <Pastille ton="muted" compacte>retirée du plan</Pastille>}
        </div>
      ))}
    </div>
  );
}
